import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Switch,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Space,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  fetchTaskVersions,
  saveTaskVersion,
  setTaskVersionEnabled,
} from '../../store/slices/taskVersionSlice';
import { TaskVersion } from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const TaskVersionManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const taskVersionState = useSelector((state: RootState) => state.taskVersion);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);
  const [versionForm] = Form.useForm<TaskVersion>();
  const [versionModalVisible, setVersionModalVisible] = useState(false);
  const [editingVersion, setEditingVersion] = useState<TaskVersion | null>(null);

  const refreshData = useCallback(() => {
    dispatch(fetchTaskVersions());
  }, [dispatch]);

  // 自动刷新
  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: refreshData,
  });

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const openCreateVersionModal = () => {
    setEditingVersion(null);
    versionForm.resetFields();
    setVersionModalVisible(true);
  };

  const openEditVersionModal = (record: TaskVersion) => {
    setEditingVersion(record);
    versionForm.setFieldsValue({
      taskType: record.taskType,
      version: record.version,
      description: record.description,
      upgradeScriptUrl: record.upgradeScriptUrl,
      enabled: record.enabled,
    } as any);
    setVersionModalVisible(true);
  };

  const handleVersionSave = async (values: any) => {
    try {
      await dispatch(
        saveTaskVersion({
          taskType: Number(values.taskType),
          version: values.version,
          description: values.description,
          upgradeScriptUrl: values.upgradeScriptUrl,
          enabled: values.enabled ?? true,
        })
      ).unwrap();
      message.success('任务版本已保存');
      setVersionModalVisible(false);
      setEditingVersion(null);
      versionForm.resetFields();
      refreshData();
    } catch (error) {
      message.error('保存任务版本失败');
    }
  };

  const handleToggleVersionEnabled = async (record: TaskVersion) => {
    const newEnabled = !record.enabled;
    try {
      await dispatch(
        setTaskVersionEnabled({ taskType: record.taskType, enabled: newEnabled })
      ).unwrap();
      message.success(newEnabled ? '已启用该任务版本' : '已禁用该任务版本');
    } catch (error) {
      message.error('更新任务版本状态失败');
    }
  };

  const columns = [
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 100,
      sorter: (a: TaskVersion, b: TaskVersion) => a.taskType - b.taskType,
    },
    {
      title: '任务名称',
      dataIndex: 'taskTypeName',
      key: 'taskTypeName',
      width: 150,
      render: (text: string | undefined) => text || '-',
    },
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 120,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string | undefined) => text || '-',
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (_: any, record: TaskVersion) => (
        <Switch
          checked={record.enabled}
          onChange={() => handleToggleVersionEnabled(record)}
          size="small"
        />
      ),
    },
    {
      title: '升级脚本URL',
      dataIndex: 'upgradeScriptUrl',
      key: 'upgradeScriptUrl',
      ellipsis: true,
      render: (text: string | undefined) => (
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer" title={text}>
            {text.length > 50 ? `${text.substring(0, 50)}...` : text}
          </a>
        ) : '-'
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (ts: number) =>
        ts ? new Date(ts).toLocaleString() : '-',
      sorter: (a: TaskVersion, b: TaskVersion) => a.updatedAt - b.updatedAt,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: TaskVersion) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => openEditVersionModal(record)}
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>
          任务版本管理
        </h1>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateVersionModal}
            size={window.innerWidth <= 768 ? 'small' : 'middle'}
          >
            新增版本
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshData}
            loading={taskVersionState.loading}
            size={window.innerWidth <= 768 ? 'small' : 'middle'}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          rowKey="id"
          loading={taskVersionState.loading}
          dataSource={taskVersionState.list}
          columns={columns}
          scroll={{ x: window.innerWidth <= 768 ? 800 : 1200 }}
          pagination={{
            pageSize: window.innerWidth <= 768 ? 10 : 20,
            showSizeChanger: window.innerWidth > 768,
            showQuickJumper: window.innerWidth > 768,
            showTotal: (total) => `共 ${total} 条记录`,
            size: window.innerWidth <= 768 ? 'small' : 'default',
          }}
          size={window.innerWidth <= 768 ? 'small' : 'middle'}
        />
      </Card>

      {/* 任务版本编辑弹窗 */}
      <Modal
        title={editingVersion ? '编辑任务版本' : '新增任务版本'}
        open={versionModalVisible}
        onCancel={() => {
          setVersionModalVisible(false);
          setEditingVersion(null);
          versionForm.resetFields();
        }}
        footer={null}
        width={window.innerWidth <= 768 ? '95%' : 600}
        style={{ top: window.innerWidth <= 768 ? 20 : 100 }}
      >
        <Form
          form={versionForm}
          layout="vertical"
          onFinish={handleVersionSave}
        >
          <Form.Item
            name="taskType"
            label="任务类型代码"
            rules={[{ required: true, message: '请输入任务类型代码' }]}
          >
            <InputNumber
              placeholder="请输入任务类型代码"
              style={{ width: '100%' }}
              disabled={!!editingVersion}
              min={1}
            />
          </Form.Item>
          <Form.Item
            name="version"
            label="版本号"
            rules={[
              { required: true, message: '请输入版本号' },
              {
                pattern: /^\d+\.\d+\.\d+$/,
                message: '版本号格式应为：主版本号.次版本号.修订号（如：1.2.3）',
              },
            ]}
          >
            <Input placeholder="例如：1.2.3" />
          </Form.Item>
          <Form.Item
            name="description"
            label="版本描述"
          >
            <Input.TextArea
              placeholder="请输入版本描述"
              rows={3}
            />
          </Form.Item>
          <Form.Item
            name="upgradeScriptUrl"
            label="升级脚本URL"
            rules={[
              {
                type: 'url',
                message: '请输入有效的URL地址',
              },
            ]}
          >
            <Input placeholder="https://example.com/upgrade-scripts/script.sh" />
          </Form.Item>
          <Form.Item
            name="enabled"
            label="是否启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setVersionModalVisible(false);
                  setEditingVersion(null);
                  versionForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={taskVersionState.saving}>
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskVersionManagement;

